import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  LOCALE_ID,
  signal,
  untracked,
} from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { EMPTY, firstValueFrom, from, lastValueFrom, merge, Observable, of, Subject } from "rxjs";
import {
  catchError,
  debounceTime,
  finalize,
  map,
  mergeMap,
  shareReplay,
  switchMap,
  tap,
} from "rxjs/operators";

import { ClickOutsideReason } from "@bitwarden/angular/directives/click-outside.directive";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { Utils } from "@bitwarden/common/misc/utils";
import { GlobalService } from "@bitwarden/common/services/global/global.service";
import { PlatformUtilsService } from "@vault/common/abstractions/platformUtils.service";

import { GuideSuccessToastComponent } from "@bitwarden/web-vault/app/components/guide-success-toast/guide-success-toast.component";
import { GroceryHelpDialogComponent } from "@bitwarden/web-vault/app/gloss/gloss-grocery/grocery-help-dialog/grocery-help-dialog.component";
import {
  GroceryService,
  GroceryListStatus,
  PAGE_SIZE,
} from "@bitwarden/web-vault/app/services/grocery/grocery.service";
import { GlossExtensionService } from "@bitwarden/web-vault/app/services/extension/gloss-extension.service";
import {
  GROCERY_PLUGIN_NAME,
  PluginConfigService,
} from "@bitwarden/web-vault/app/services/store/data-plugin/plugin-config.service";
import {
  DialogWrapperComponent,
  DialogWrapperData,
} from "@bitwarden/web-vault/app/components/wrappers/dialog-wrapper/dialog-wrapper.component";
import { GLOBAL_BASE_CURRENCY } from "@bitwarden/web-vault/app/models/constants/global.constants";
import { RightPanelService } from "@bitwarden/web-vault/app/components/dialog/right-panel.service";
import { formatCurrencyAmount } from "@bitwarden/web-vault/app/shared/utils/helper-number-format";
import { prefersReducedMotion } from "@bitwarden/web-vault/app/shared/utils/reduced-motion.util";
import { RenameListDialogComponent } from "@bitwarden/web-vault/app/gloss/gloss-grocery/rename-list-dialog/rename-list-dialog.component";
import {
  GroceryRowAction,
  GroceryRowActionsSheetComponent,
  GroceryRowActionsSheetData,
} from "@bitwarden/web-vault/app/gloss/gloss-grocery/grocery-row-actions-sheet/grocery-row-actions-sheet.component";
import { SM_BREAKPOINT, observeBreakpoint } from "@bitwarden/web-vault/app/shared/breakpoints";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { RoleAccessService } from "@bitwarden/web-vault/app/services/permission/role-access.service";
import { ConfirmationEnum } from "@bitwarden/web-vault/app/models/enum/confirmation.enum";
import {
  ComparisonSummary,
  GroceryItem,
  GroceryResultRow,
  GroceryStoreKey,
  ShoppingRow,
  StorePriceChip,
  StoreTotal,
} from "@bitwarden/web-vault/app/services/grocery/grocery.types";
import {
  ShoppingList,
  ShoppingListItem,
} from "@bitwarden/web-vault/app/services/grocery/shopping-list.types";
import {
  bestValueIds,
  buildResultRow,
  cheapestStore,
  GROCERY_STORES,
  latestPriceAsOf,
  listItemDedupKey,
  priceChips,
  relativeTime,
} from "@bitwarden/web-vault/app/services/grocery/grocery.utils";
import { extractLivePrice } from "@bitwarden/web-vault/app/services/grocery/price-extract";
import { isAllowedStoreUrl } from "@bitwarden/web-vault/app/services/grocery/store-url-allowlist";
import { capFetch } from "@bitwarden/web-vault/utils/fetch";

/** View-model for the current search: loading / error / results-so-far / more-available. */
interface SearchVm {
  loading: boolean;
  error: boolean;
  rows: GroceryResultRow[];
  hasMore: boolean;
  /** Total matches for the active query — drives the "PRICED MATCHES · N" / "Show all N" copy. */
  total: number;
}

/**
 * One section of the "Best" list: the rows whose cheapest store is `key`, or the "awaiting price"
 * bucket for rows with no price anywhere. `subtotal` is null for the latter.
 */
interface StoreGroup {
  key: GroceryStoreKey | "awaiting";
  label: string;
  rows: ShoppingRow[];
  subtotal: number | null;
}

const EMPTY_SEARCH_VM: SearchVm = {
  loading: false,
  error: false,
  rows: [],
  hasMore: false,
  total: 0,
};

/** Shape returned by the Gloss browser extension's background fetch (see gloss-extension). */
interface ExtensionFetchResult {
  ok: boolean;
  status?: number;
  bytes?: number;
  price?: number | null;
  priceSource?: string | null;
  blocked?: boolean;
  identity?: { name?: string | null; barcode?: string | null; stockcode?: string | null };
  error?: string;
}

/** Per-item "refresh prices" state: spinner + result message for one row. */
interface RefreshEntry {
  loading: boolean;
  error?: boolean;
  message?: string;
  /**
   * True when the refresh was blocked only because the Gloss web extension is
   * absent. The row/card shows an inline "Install extension" CTA in that case —
   * the gate no longer redirects to /plugins itself (#5942).
   */
  extensionMissing?: boolean;
}

/** Debounce so a burst of keystrokes fires one search call, not one per keystroke. */
const SEARCH_DEBOUNCE_MS = 300;

/** Minimum query length before a search is issued (matches the backend). */
const SEARCH_MIN_LENGTH = 2;

/** Un-ticked item names listed in the archive confirm before collapsing the rest into "and N more". */
const ARCHIVE_CONFIRM_MAX_NAMES = 3;

/** Trim + lower-case a raw input value into the query actually sent to the backend. */
const normalizeQuery = (value: string): string => value.trim().toLowerCase();

/** True if any store's price differs between the two maps (over the union of their stores). */
const pricesChanged = (
  before: Partial<Record<GroceryStoreKey, number>>,
  after: Partial<Record<GroceryStoreKey, number>>,
): boolean =>
  (Object.keys({ ...before, ...after }) as GroceryStoreKey[]).some(
    (store) => before[store] !== after[store],
  );

/**
 * "Gloss Grocery" page: search the product catalog on demand, build
 * a list, and compare per-store totals. Gated on the grocery plugin being
 * installed (see groceryPluginInstalled).
 * This component only renders and forwards user intent and owns the search
 * debounce/pagination UI state — all catalog data and comparison maths live
 * in {@link GroceryService}.
 */
@Component({
  selector: "app-gloss-grocery",
  templateUrl: "./gloss-grocery.component.html",
})
export class GlossGroceryComponent {
  private readonly rightPanel = inject(RightPanelService);
  private readonly grocery = inject(GroceryService);
  private readonly extensionService = inject(GlossExtensionService);
  private readonly pluginConfigService = inject(PluginConfigService);
  private readonly router = inject(Router);

  /**
   * Whether the Grocery plugin is installed (its vault plugin row exists). The whole page is
   * gated on this: when false, the template shows only a "install the plugin" prompt with a
   * button into the Plugins store. Reactive because hasGroceryPluginConfig() reads the vault
   * plugin/key store signals, so enabling the plugin (then returning here) flips the page live.
   */
  readonly groceryPluginInstalled = computed(() =>
    this.pluginConfigService.hasGroceryPluginConfig(),
  );
  private readonly platformUtils = inject(PlatformUtilsService);
  private readonly i18nService = inject(I18nService);
  private readonly globalService = inject(GlobalService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly roleAccessService = inject(RoleAccessService);
  private readonly confirmation = inject(ConfirmationDialogService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly localeId = inject(LOCALE_ID);

  /** Tracks focus within the search wrapper — see onSearchClickOutside. */
  private isSearchFocused = false;

  // Viewport width only (not observeSmallScreen, which also ORs in UA-detected mobile): the
  // sheet's CSS and the template's `sm:` classes switch on this exact breakpoint, so a phone in
  // landscape (>=640px) must keep the "…" menu rather than get an unstyled sheet.
  protected readonly isSmallScreen = observeBreakpoint(SM_BREAKPOINT);

  /** True while an archive (confirm + archive + follow-on create) is in flight. */
  readonly archiving = signal(false);

  /** The open rename dialog, so its inner form can close it via a callback. */
  private renameDialogRef?: MatDialogRef<DialogWrapperComponent>;

  /**
   * True on the native iOS/Android app. There, live prices are fetched
   * natively via Capacitor (no CORS, device IP), so the Gloss browser
   * extension isn't required — it doesn't even exist on mobile.
   */
  private get isMobileApp(): boolean {
    return this.platformUtils.isMobileDevice();
  }

  /**
   * Live "is the Gloss Vault extension installed" flag. Search itself is not
   * gated on it (search hits the backend directly); only the web live-price
   * refresh action is — see refreshRow.
   */
  readonly extensionInstalled = toSignal(this.extensionService.installedChanges(), {
    initialValue: this.extensionService.isInstalled(),
  });

  /** Raw keystrokes; debounced before triggering a search call. */
  private readonly queryInput$ = new Subject<string>();

  /** "Show more" clicks; each fetches the next page of the active query. */
  private readonly loadMore$ = new Subject<void>();

  /** Requests to (re)load the Past lists section; switchMap drops stale responses. */
  private readonly loadPastLists$ = new Subject<void>();

  /** The query the currently-shown page of results belongs to (for "load more"). */
  private activeQuery = "";

  /**
   * True server-side offset to request next for the active query — how many
   * items the server has actually returned so far, not `rows.length` (which
   * can undercount once client-side dedup drops a duplicate row from a page).
   * Tracking it separately keeps "load more" from re-requesting and
   * re-filtering the same already-seen items.
   */
  private nextSearchOffset = 0;

  /** Two-way bound value for the search input. */
  searchQuery = "";

  /** Which store's "missing items" popover is currently open, if any. */
  readonly openPopover = signal<GroceryStoreKey | null>(null);

  /** Which list row's "…" action menu is currently open, if any (by list-item id). */
  readonly openRowMenu = signal<string | null>(null);

  /** Whether the press behind the current row click began with a popup open — see onRowClick. */
  private pressStartedWithPopupOpen = false;

  /** List-item ids with an "Archive this item" still in flight — blocks a repeat click from archiving twice. */
  readonly archivingItems = signal<ReadonlySet<string>>(new Set());

  readonly searchVm = signal<SearchVm>(EMPTY_SEARCH_VM);

  /** True once "Show all N matches" has been clicked — reveals every fetched row, not just the top 5. */
  readonly searchDropdownExpanded = signal(false);

  /** The catalog rows currently rendered in the dropdown — top 5 until expanded. */
  readonly visibleSearchRows = computed<GroceryResultRow[]>(() => {
    const rows = this.searchVm().rows;
    return this.searchDropdownExpanded()
      ? rows
      : rows.slice(0, GlossGroceryComponent.SEARCH_DROPDOWN_PREVIEW_COUNT);
  });

  /** True once "Show all" has hit MAX_SHOW_ALL_ROWS with more still on the server. */
  readonly searchDropdownAtCap = computed(
    () => this.searchVm().rows.length >= GlossGroceryComponent.MAX_SHOW_ALL_ROWS,
  );

  /** True after an auto-paged "show all" fetch fails — stops the retry effect and shows a retry affordance. */
  readonly searchLoadMoreFailed = signal(false);

  /** Per-item "refresh prices" state (spinner + result message), keyed by item id. */
  readonly refreshState = signal<Record<string, RefreshEntry>>({});

  /** Item ids the user has requested a price for this session — drives the per-item confirmation. */
  readonly priceRequested = signal<Set<string>>(new Set());

  /**
   * Shared class list for a clickable search-dropdown row (the "Add custom"
   * row and each catalog match row) — kept as one constant so the two near-
   * identical row wrappers can't drift apart on a future style tweak.
   *
   * Rows using this class carry `appKeyboardActivatable` (role/tabindex +
   * Enter/Space activation, see KeyboardActivatableDirective) plus their own
   * `(click)` handler, and they nest real interactive elements (the "Add"
   * pill, the refresh icon button). Every such nested control MUST call
   * `$event.stopPropagation()` on click *and* on keydown.enter/keydown.space,
   * or activating it will also bubble up into the row's own handlers (e.g.
   * double-adding the item).
   */
  protected readonly clickableSearchRowClass =
    "tw-flex tw-gap-2.5 tw-items-center sm:tw-flex-wrap tw-px-4 tw-py-3 tw-w-full tw-cursor-pointer hover:tw-bg-color-surface-elevated";

  /** Number of catalog matches shown before "Show all N matches" is clicked. */
  private static readonly SEARCH_DROPDOWN_PREVIEW_COUNT = 5;

  /** Hard ceiling on rows auto-fetched by "Show all", so a broad query can't page forever — ten server pages' worth. */
  private static readonly MAX_SHOW_ALL_ROWS = PAGE_SIZE * 10;

  /** Timeout for a single store's live price fetch through the extension. */
  private static readonly REFRESH_TIMEOUT_MS = 4000;

  /** Max rows refreshed at once by "Refresh Price" — caps concurrent extension/backend calls on large lists. */
  private static readonly REFRESH_ALL_CONCURRENCY = 5;

  /** Shared message shown on a row (or every row, for the bulk action) when live fetch isn't available. */
  private static readonly EXTENSION_REQUIRED_MESSAGE =
    "Install the Gloss extension to refresh live prices.";

  /** Item ids with a `refreshRow` call currently in flight — guards against a manual click re-entering a row already being refreshed by the bulk sweep (or another manual click). */
  private readonly refreshingRowIds = new Set<string>();

  // shareReplay so a signal resubscribe replays the last computed rows
  // instead of re-running buildResultRows against the underlying BehaviorSubject.
  // Normalizes a null emission (no active list yet) to [] here, once, so every
  // consumer — the listRows signal and its many `.length`/`.map` template
  // usages — can treat it as a plain array without its own null guard.
  private readonly list$: Observable<ShoppingRow[]> = this.grocery.getList().pipe(
    map((rows) => rows ?? []),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  private readonly summary$: Observable<ComparisonSummary | null> = this.grocery.getSummary();

  /**
   * The list, as a signal rather than `list$ | async` — so the template can
   * read it directly without wrapping the whole page (including the always-
   * available search UI) in an `@if` gated on the list stream ever emitting.
   */
  readonly listRows = toSignal(
    // list$ already collapses the stream's null (no list loaded — see GroceryService.getList)
    // to []. "No list yet / load failed" is still distinguishable via listStatus.
    this.list$,
    { initialValue: [] as ShoppingRow[] },
  );

  /** The comparison summary as a signal — used by the Price Comparison card. */
  readonly summary = toSignal(this.summary$, { initialValue: null as ComparisonSummary | null });

  /**
   * Store labels joined for the empty-state hint, so it stays correct if GROCERY_STORES
   * changes. The joiner word comes from i18n (not a hardcoded "and") so it translates.
   */
  readonly comparisonStoreNames = GlossGroceryComponent.joinLabels(
    GROCERY_STORES.map(({ label }) => label),
    this.i18nService.t("groceryListJoinerAnd"),
  );

  /**
   * "Prices updated N ago" label for the comparison card, from the freshest
   * store price on the list; `null` only when nothing has ever been priced.
   * relativeTime() also returns "" for a timestamp a hair in the future
   * (clock skew) — since latestPriceAsOf found a real timestamp here, that's
   * "just now", not "never updated", so it's not treated as missing. Derived
   * from `listRows` (rather than its own `list$` subscription) so there's a
   * single source of truth for the list, computed on demand.
   */
  readonly latestPriceLabel = computed<string | null>(() => {
    const latest = latestPriceAsOf(this.listRows().map((row) => row.item));
    return latest ? relativeTime(latest) || "just now" : null;
  });

  /**
   * Store label combo for the "Best if split across stores" tile (e.g. "Aldi + Woolies") —
   * every store that supplies at least one item's cheapest price, in the usual display order.
   */
  readonly splitStoresLabel = computed<string>(() => {
    const stores = new Set<GroceryStoreKey>();
    for (const row of this.listRows()) {
      const best = cheapestStore(row.item.prices);
      if (best) {
        stores.add(best);
      }
    }
    return GROCERY_STORES.filter((s) => stores.has(s.key))
      .map((s) => s.label)
      .join(" + ");
  });

  /**
   * The store locked as the default tab (session-only — there's no backend field for this
   * yet). Locking a store also switches the active tab to it. The Price Comparison card no
   * longer has a control to lock a store, so this stays null until a new entry point exists.
   */
  readonly defaultStore = signal<GroceryStoreKey | null>(null);

  /** Store tabs shown above the list: "Best" (cheapest per item) + each store. */
  readonly storeTabs: readonly { key: GroceryStoreKey | "best"; label: string }[] = [
    { key: "best", label: this.i18nService.t("groceryBestTab") },
    ...GROCERY_STORES,
  ];

  /** Which store tab is active — filters the price chip shown on each row + the footer total. */
  readonly selectedStore = signal<GroceryStoreKey | "best">("best");

  /** Display label for the active store tab (e.g. "Coles", or "Best"). */
  readonly selectedStoreTabLabel = computed<string>(
    () => this.storeTabs.find((tab) => tab.key === this.selectedStore())?.label ?? "",
  );

  /**
   * The active store tab's entry in summary.storeTotals — null for "Best" (which has no
   * single store to look up) or before the summary loads. Shared by footerTotal and
   * footerNotCarriedCount so both read off one lookup instead of scanning storeTotals twice.
   */
  private readonly activeStoreTotal = computed<StoreTotal | null>(() => {
    const store = this.selectedStore();
    if (store === "best") {
      return null;
    }
    return this.summary()?.storeTotals.find((total) => total.store === store) ?? null;
  });

  /** Footer total for the active store tab: that store's running total, or the per-item cheapest sum for "Best". */
  readonly footerTotal = computed<number>(() => {
    if (this.selectedStore() === "best") {
      return this.summary()?.splitTotal ?? 0;
    }
    return this.activeStoreTotal()?.total ?? 0;
  });

  /**
   * Items the active store tab doesn't carry — 0 for "Best" (which has no single
   * store to exclude from). Counts catalog items with no price at the store, which
   * includes ones priced nowhere. Drives the mobile footer's "Excludes N not carried" hint.
   */
  readonly footerNotCarriedCount = computed<number>(
    () => this.activeStoreTotal()?.missing.length ?? 0,
  );

  /**
   * Custom items on the list with no catalog price yet — they're never part of any
   * total, so this applies on every tab, "Best" included. Drives the mobile footer's
   * "N awaiting price" hint.
   */
  readonly footerAwaitingPriceCount = computed<number>(
    () => this.listRows().filter((row) => row.isCustom).length,
  );

  /**
   * The list, filtered to the active store tab — "filter by store to plan each trip": switching
   * to a store hides items it doesn't carry, so the visible list is exactly what to buy there.
   * Rows with no store to be "carried" by — custom/unlisted items, and catalog items with no
   * known price anywhere — stay visible on every tab: they have no store to be filtered against,
   * and hiding them would also hide the only copy of their remove/tick controls, stranding the
   * item on the list until the whole list is archived. Under a store tab, priced catalog items
   * that store doesn't carry are hidden — except under the locked default store, where nothing
   * is hidden at all, so a not-carried item shows a "Not carried" chip
   * instead of silently dropping off the trip.
   */
  readonly visibleListRows = computed<ShoppingRow[]>(() => {
    const store = this.selectedStore();
    if (this.defaultStore() === store) {
      return this.listRows();
    }
    return this.listRows().filter((row) => {
      if (row.isCustom || row.chips.length === 0) {
        return true;
      }
      return store === "best" || row.chips.some((chip) => chip.store === store);
    });
  });

  /**
   * The visible rows grouped by the store each is cheapest at — drives the "Best" tab, where every
   * item shows just that one store's chip under a "STORE · N items" header with the store's
   * subtotal. Rows with no price anywhere (custom items, unpriced catalog items) land in a
   * trailing "awaiting price" group. Groups follow GROCERY_STORES order; empty ones are dropped.
   */
  readonly bestStoreGroups = computed<StoreGroup[]>(() => {
    const byStore = new Map<GroceryStoreKey, { rows: ShoppingRow[]; subtotal: number }>();
    const awaiting: ShoppingRow[] = [];
    for (const row of this.visibleListRows()) {
      const chip = this.cheapestChip(row);
      if (row.isCustom || !chip) {
        awaiting.push(row);
      } else {
        const group = byStore.get(chip.store) ?? { rows: [], subtotal: 0 };
        group.rows.push(row);
        group.subtotal += chip.price;
        byStore.set(chip.store, group);
      }
    }
    const groups: StoreGroup[] = GROCERY_STORES.filter(({ key }) => byStore.has(key)).map(
      ({ key, label }) => ({ key, label, ...byStore.get(key)! }),
    );
    if (awaiting.length > 0) {
      groups.push({
        key: "awaiting",
        label: this.i18nService.t("groceryGroupAwaitingPrice"),
        rows: awaiting,
        subtotal: null,
      });
    }
    return groups;
  });

  /** The current active list — drives the list header name + save button. */
  readonly activeList = toSignal(this.grocery.getActiveList(), {
    initialValue: null,
  });

  /**
   * Load status of the active list, so the template renders loading / empty /
   * error as three distinct states instead of showing "your list is empty" for a
   * list that is still loading or failed to load.
   */
  readonly listStatus = toSignal(this.grocery.getStatus(), {
    initialValue: "loading" as GroceryListStatus,
  });

  /**
   * Whether the "My Grocery List" card's body (search/tabs/rows) is expanded — mobile only
   * (the card is never collapsible on desktop; see the `sm:` classes in the template).
   */
  readonly mobileListExpanded = signal(true);

  /** Tailwind class for the "My Grocery List" card's collapsible pieces (body, divider,
   *  footer) — hidden on mobile while the card is collapsed, a no-op on desktop. */
  readonly mobileListHiddenClass = computed(() =>
    this.mobileListExpanded() ? "" : "sm:tw-hidden",
  );

  /**
   * Whether the "Price Comparison" card's body is expanded — mobile only (the card is never
   * collapsible on desktop). Starts expanded on mobile, same as the "My Grocery List" card
   * (see mobileListExpanded) — both cards open expanded and collapse on tap.
   */
  readonly mobilePriceComparisonExpanded = signal(true);

  /** Tailwind class for the "Price Comparison" card's collapsible pieces (store rows, notes,
   *  freshness row, divider, split section) — hidden on mobile while the card is collapsed, a
   *  no-op on desktop. */
  readonly mobilePriceComparisonHiddenClass = computed(() =>
    this.mobilePriceComparisonExpanded() ? "" : "sm:tw-hidden",
  );

  /** Archived lists for the "Past lists" section (lazy-loaded when expanded). */
  readonly pastLists = signal<ShoppingList[]>([]);
  /**
   * Load status of the Past lists section, so the template can tell loading /
   * empty / error apart instead of showing "No past lists yet." for a section
   * that is still loading or failed to load (mirrors {@link listStatus}).
   */
  readonly pastListsStatus = signal<"idle" | "loading" | "loaded" | "error">("idle");
  readonly showPastLists = signal(false);
  /**
   * One display row per archived list, derived once per {@link pastLists} change
   * rather than on every change-detection pass. Rows whose date label collides
   * with another's get the time of day appended so each row (and its delete
   * confirm) is unambiguous.
   */
  readonly pastListRows = computed(() => {
    const lists = this.pastLists();
    const labels = lists.map((list) => this.pastListDateLabel(list));
    return lists.map((list, i) => {
      const total = this.pastListTotal(list);
      return {
        list,
        label: labels.some((label, j) => j !== i && label === labels[i])
          ? this.pastListDateLabel(list, true)
          : labels[i],
        itemCount: list.items.length,
        tickedCount: list.items.filter((item) => item.purchased).length,
        totalLabel: total === null ? "—" : this.formatPrice(total),
      };
    });
  });
  /** Which past list is expanded to show its items (null = none). */
  readonly expandedPastListId = signal<string | null>(null);
  /**
   * Id of the past list whose "Copy to my list" is currently in flight (null =
   * none). While a copy runs, every copy button is disabled and the handler
   * short-circuits any second click, so a double-tap (on this or another past
   * list) can't fire two copies off the same click-time dedup snapshot and
   * duplicate custom (id-less) items.
   */
  readonly copyingPastListId = signal<string | null>(null);
  /**
   * True while a per-item "Add" is in flight. Same double-tap guard as
   * {@link copyingPastListId}: dedup reads the active list, which only updates
   * once the POST returns, so a second click would re-add the item (and, with no
   * active list, POST a second active list).
   */
  readonly addingPastItem = signal(false);
  /**
   * True while any copy-back (whole list or single item) is in flight. The two
   * share one lock so an "Add" racing an "Add all" can't both pass dedup.
   */
  readonly pastCopyBusy = computed(
    () => this.copyingPastListId() !== null || this.addingPastItem(),
  );
  /**
   * True while the current list has no data yet because its first load is still
   * running — the service refuses copies then, so the copy-back buttons are
   * disabled rather than letting a click end in an error toast.
   */
  readonly pastCopyUnavailable = computed(
    () => this.listStatus() === "loading" && this.activeList() === null,
  );
  /** Dedup keys of every item on the current list (see {@link listItemDedupKey}). */
  private readonly activeItemKeys = computed(
    () => new Set((this.activeList()?.items ?? []).map(listItemDedupKey)),
  );
  /**
   * Cheapest-store chip per archived-list item (keyed by item id), derived once
   * per change of the past lists rather than on every change-detection pass.
   */
  private readonly pastItemCheapestById = computed(() => {
    const result = new Map<string, StorePriceChip | null>();
    for (const list of this.pastLists()) {
      for (const item of list.items) {
        result.set(
          item._id,
          priceChips(item.prices ?? {}, item.priceAsOf).find((chip) => chip.isHighlighted) ?? null,
        );
      }
    }
    return result;
  });
  /**
   * Per archived list, the items "Add all" will add — derived once per change of
   * the past lists / current list rather than on every change-detection pass.
   */
  private readonly addableByPastListId = computed(() => {
    const result = new Map<string, ShoppingListItem[]>();
    for (const list of this.pastLists()) {
      const seen = new Set(this.activeItemKeys());
      result.set(
        list._id,
        list.items.filter((item) => {
          const key = listItemDedupKey(item);
          if (seen.has(key)) {
            return false;
          }
          seen.add(key);
          return true;
        }),
      );
    }
    return result;
  });
  /**
   * Id of the past list whose delete is currently in flight (null = none).
   * While a delete runs its Delete button is disabled and the handler
   * short-circuits any second click, so two confirms can't race — the second
   * would 404 on an already-removed list and toast a spurious failure.
   */
  readonly deletingPastListId = signal<string | null>(null);

  /** Whether the "Archived Lists" card's body is expanded — mobile only, same pattern as
   *  mobileListExpanded/mobilePriceComparisonExpanded. */
  readonly mobileArchivedListsExpanded = signal(true);

  /** `_id`s of the current-list rows just added from an archived list (single "Add" or "Add all")
   *  — each matching row gets a temporary green outline while it is in this set. Keyed by list
   *  item id so custom items (no catalog id) highlight too. */
  readonly highlightedListItemIds = signal<ReadonlySet<string>>(new Set());
  private highlightTimeoutId: ReturnType<typeof setTimeout> | null = null;

  /** True while the Price Comparison card's "Refresh Price" bulk action is in flight. */
  readonly refreshingAll = signal(false);

  /**
   * Rows saved during the current bulk sweep, keyed by item id, mapped to their
   * prices before that row's refresh started. Filled by refreshRow for both the
   * sweep's own calls and a manual row click racing it (whose toast is
   * suppressed), so the sweep's aggregate toast still counts it. Unset outside a sweep.
   */
  private sweepSaved?: Map<string, Partial<Record<GroceryStoreKey, number>>>;

  /**
   * Bumped when a bulk sweep starts and again when it ends, so refreshRow can
   * tell a manual call overlapped a sweep even if it finished after the sweep.
   */
  private sweepEpoch = 0;

  /** True when at least one list row is bulk-refreshable (has a source URL) — false for an empty list or one made up entirely of custom items. */
  readonly hasRefreshableRows = computed(() => this.listRows().some((row) => !row.isCustom));

  /**
   * A signal (not a plain field) so the constructor's auto-pager `effect()`
   * — which only re-runs when a signal it reads changes — is guaranteed to
   * see the current value on every re-run, instead of depending on this
   * being written and the effect re-executing in a particular order.
   */
  private readonly loadingMore = signal(false);

  /** Ids currently on the list — the persistent half of "what's visible", used to prune refreshState. */
  private listItemIds = new Set<string>();

  /**
   * The last query we actually acted on, normalized (trimmed + lower-cased), or
   * `null` when there is no key yet / it was deliberately reset. Guards against
   * re-searching when only whitespace or case changed — e.g. pressing space after
   * a term turns "tim" into "tim " but the effective query is unchanged, so we
   * must not fire another identical request (or wipe the shown results back to a
   * loading state). `null` (not "") is the reset value on purpose: "" is itself a
   * real normalized query — an empty search box — so overloading it would let the
   * dedup guard swallow the "clear the box" path (e.g. dismissing an error banner
   * or clearing stale results). Reset to `null` by addItem, the error branch, and
   * removeItem so re-searching / clearing after any of them still works.
   */
  private lastNormalizedQuery: string | null = null;

  // Set once the component is torn down, so an async action that resolves after the user has left the
  // page (e.g. the ~1.5s extension check in ensureLiveFetchAvailable) can skip its toast (#5695).
  private destroyed = false;

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
      if (this.highlightTimeoutId) {
        clearTimeout(this.highlightTimeoutId);
      }
    });
    // Re-ping in case the extension was installed since app load.
    void this.extensionService.check();
    // Track list membership so refreshState can be pruned to only visible items
    // whenever the list changes (add/remove).
    this.list$.pipe(takeUntilDestroyed()).subscribe((rows) => {
      this.listItemIds = new Set(rows.map((r) => r.item.id));
      this.pruneRefreshState();
    });
    // Every search — a fresh keystroke query (from the top) or a "load more"
    // (next page of the active query) — flows through a single switchMap, so the
    // newest one cancels any request still in flight and always wins.
    // De-duping of unchanged queries happens in onSearchChange (on the
    // normalized query), not here: keeping the pipeline free of
    // distinctUntilChanged means a genuine repeat — after addItem() resets the
    // dedup key — still runs.
    merge(
      this.queryInput$.pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        map((query) => ({ query, offset: 0 })),
      ),
      this.loadMore$.pipe(map(() => ({ query: this.activeQuery, offset: this.nextSearchOffset }))),
    )
      .pipe(
        switchMap(({ query, offset }) => this.runSearch$(query, offset)),
        takeUntilDestroyed(),
      )
      .subscribe();
    // "Show all" auto-pages until hasMore is false: loadMore() writes searchVm,
    // which this effect reads, so it re-runs itself page after page.
    // searchLoadMoreFailed stops that loop once a page fails or stalls.
    effect(
      () => {
        const vm = this.searchVm();
        if (
          this.searchDropdownExpanded() &&
          vm.hasMore &&
          vm.rows.length < GlossGroceryComponent.MAX_SHOW_ALL_ROWS &&
          !this.loadingMore() &&
          !this.searchLoadMoreFailed()
        ) {
          this.loadMore();
        }
      },
      { allowSignalWrites: true },
    );
    // Past lists loads flow through a single switchMap so a slow response can't
    // write after destroy, and a rapid hide/show (or a refetch after archive)
    // never lets an older request overwrite a newer one.
    this.loadPastLists$
      .pipe(
        tap(() => this.pastListsStatus.set("loading")),
        switchMap(() =>
          this.grocery.getArchivedLists().pipe(
            map((lists) => ({ lists, error: false })),
            catchError((e: unknown) => {
              // eslint-disable-next-line no-console
              console.error("Failed to load past lists", e);
              return of({ lists: [] as ShoppingList[], error: true });
            }),
          ),
        ),
        takeUntilDestroyed(),
      )
      .subscribe(({ lists, error }) => {
        if (error) {
          // Flag the failure so the template shows an error, not a misleading
          // "no past lists". Leave pastLists as-is (don't clear to []) so a
          // retry doesn't have to rebuild from nothing.
          this.pastListsStatus.set("error");
        } else {
          this.pastLists.set(lists);
          this.pastListsStatus.set("loaded");
        }
      });
    // (Re)load the active list once the Grocery plugin is installed — driven off
    // the signal, not ngOnInit, for two reasons:
    //  - the page is gated on groceryPluginInstalled(); loading unconditionally
    //    would POST createList() for a user who only opened the page to see
    //    the install prompt (refreshActiveList -> createList when they have none).
    //  - on a cold load the plugin collection is still null at init, so a bare
    //    ngOnInit guard would never load once the plugin later shows up. The
    //    effect re-runs when the signal flips false->true and loads then.
    // untracked() keeps refreshActiveList's own signal reads out of this effect's
    // dependencies (and permits its writes without allowSignalWrites).
    effect(() => {
      if (this.groceryPluginInstalled()) {
        untracked(() => this.grocery.refreshActiveList());
      }
    });
  }

  /**
   * Sends the user to the Plugins store with the Gloss Vault extension's details dialog opened (via
   * the `pluginId` query param the Plugins page reads), so they can install it instead of hunting
   * through the full store. Called from the inline "Install extension" CTA (beta users) when a price
   * refresh needs the extension and it isn't present.
   */
  goToInstallExtension(): void {
    void this.router.navigate(["/plugins"], {
      queryParams: { pluginId: GlossExtensionService.PLUGIN_ID, tab: "store" },
    });
  }

  /**
   * Tells the user live price refresh is mobile-only. Called when a refresh needs the Gloss
   * extension on web and it isn't present (non-beta users; beta users get the inline CTA instead).
   */
  private showMobileOnlyToast(): void {
    // The extension check can take ~1.5s when the extension is absent; if the user navigated away in that
    // window, don't pop a toast over whatever page they're on now — #5695.
    if (this.destroyed) {
      return;
    }
    this.globalService.showMessage(
      "info",
      this.i18nService.t("groceryRefreshMobileOnlyToastTitle"),
      this.i18nService.t("groceryRefreshMobileOnlyToastBody"),
      false,
    );
  }

  /**
   * Sends the user to the Plugins store with the Grocery plugin's details dialog opened (via the
   * `pluginId` query param the Plugins page reads), so they can enable it from the not-installed
   * prompt on this page.
   */
  goToGroceryPlugin(): void {
    void this.router.navigate(["/plugins"], { queryParams: { pluginId: GROCERY_PLUGIN_NAME } });
  }

  /**
   * Root click handler for a list row. On a wider screen, clicking anywhere on the row ticks the
   * item off (the "…" menu owns the other actions). On small screens, where the row's actions are
   * a bottom sheet, tapping the row opens it instead. A click that lands on an inner control
   * (tick-off, request price, "…") is that control's own action, not a click on the row.
   */
  onRowClick(event: MouseEvent, row: ShoppingRow, removable: boolean): void {
    if (!removable) {
      return;
    }
    if ((event.target as Element | null)?.closest("button, a, input")) {
      return;
    }
    if (this.isSmallScreen()) {
      this.openRowActionsSheet(row, removable);
      return;
    }
    // A press that closes an open popup (the click-outside directive shuts it on mouseup, before
    // this click), or that ends a text-selection drag, is not a request to tick the item off.
    if (this.pressStartedWithPopupOpen || this.hasSelectionIn(event.currentTarget)) {
      return;
    }
    this.togglePurchased(row);
  }

  /** A click only reaches a row if its press started on that row, so this snapshot is current. */
  onRowMouseDown(): void {
    this.pressStartedWithPopupOpen =
      this.openRowMenu() !== null || this.openPopover() !== null || this.hasSearchableQuery();
  }

  private hasSelectionIn(container: EventTarget | null): boolean {
    const selection = window.getSelection();
    return (
      !!selection &&
      !selection.isCollapsed &&
      container instanceof Node &&
      container.contains(selection.anchorNode)
    );
  }

  openRowActionsSheet(row: ShoppingRow, removable: boolean): void {
    // "Best" tab has no selected store: use the chip the row itself highlights. On a store tab use
    // only that store's chip, so a "Not carried" row doesn't show another store's price.
    const chip =
      this.selectedStore() === "best"
        ? (row.chips.find((c) => c.isHighlighted) ?? null)
        : this.selectedChip(row);
    const dialogRef = this.dialog.open<
      GroceryRowActionsSheetComponent,
      GroceryRowActionsSheetData,
      GroceryRowAction | undefined
    >(GroceryRowActionsSheetComponent, {
      panelClass: ["no-background-dialog", "grocery-help-sheet"],
      maxWidth: "520px",
      width: "100vw",
      autoFocus: false,
      // Material's dialog container already supplies role="dialog" + aria-modal; label it.
      ariaLabelledBy: "grocery-row-actions-title",
      // Default is 75ms, which cuts the 200ms slide-out short (see RIGHT_PANEL_CONFIG).
      exitAnimationDuration: "200ms",
      data: {
        itemName: row.item.name,
        priceLabel: chip ? `${this.formatPrice(chip.price)} · ${chip.label}` : null,
        showRefresh: !row.isCustom,
        refreshing: computed(() => this.refreshState()[row.item.id]?.loading === true),
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((action) => {
        if (action === "refresh") {
          void this.refreshRow(row, removable);
        } else if (action === "archive") {
          this.archiveItem(row);
        } else if (action === "remove") {
          this.removeItem(row.listItemId);
        }
      });
  }

  /** Opens the "How this list works" explainer from the ⓘ beside the page title. */
  openHelpDialog(): void {
    this.dialog.open(GroceryHelpDialogComponent, {
      panelClass: ["no-background-dialog", "grocery-help-sheet"],
      maxWidth: "520px",
      width: "92vw",
      autoFocus: false,
    });
  }

  /**
   * Locale-aware price string (e.g. "A$3.45") via the app's shared currency
   * formatter, so grocery prices can't drift from how every other surface
   * (transactions, wealth comparison, gift cards) formats currency. No price
   * in this component is shown alongside an "AUD" label, so this must pass
   * currencyDisplay: "symbol" — the bare narrowSymbol default ("$") is only
   * safe when the ISO code is shown nearby (see helper-number-format.ts).
   */
  formatPrice(amount: number): string {
    return formatCurrencyAmount(amount, GLOBAL_BASE_CURRENCY, this.localeId, {
      currencyDisplay: "symbol",
    });
  }

  /** Whether the typed query is long enough to actually search (mirrors SEARCH_MIN_LENGTH). */
  hasSearchableQuery(): boolean {
    return this.searchQuery.trim().length >= SEARCH_MIN_LENGTH;
  }

  /** The search results dialog's id: the input's combobox points at it (aria-controls). */
  protected readonly searchResultsId = "grocery-search-results";

  /**
   * Arrow keys between the search combobox and its results dialog (#4825): ArrowDown from the
   * input moves to the first row, ArrowUp/ArrowDown move between rows, and ArrowUp from the first
   * row goes back to the input. Keys on anything else (e.g. a button inside a row) are left alone.
   */
  // Event, not KeyboardEvent: that's how Angular types a (keydown.arrowdown) template $event.
  onSearchArrowKey(event: Event): void {
    const results = document.getElementById(this.searchResultsId);
    const input = results?.parentElement?.querySelector<HTMLInputElement>("input");
    if (!results || !input) {
      return;
    }
    const rows = Array.from(results.querySelectorAll<HTMLElement>('[tabindex="0"]'));
    const down = (event as KeyboardEvent).key === "ArrowDown";
    let next: HTMLElement | undefined;
    if (event.target === input) {
      next = down ? rows[0] : undefined;
    } else {
      const index = rows.indexOf(event.target as HTMLElement);
      if (index !== -1) {
        next = down ? rows[index + 1] : index === 0 ? input : rows[index - 1];
      }
    }
    if (next) {
      event.preventDefault();
      next.focus();
    }
  }

  /**
   * Called before a change that closes the results dialog (an add, Escape): if focus is in it, it
   * would be lost to <body> with the dialog, so it goes back to the search input instead.
   */
  private refocusSearchIfInResults(): void {
    const results = document.getElementById(this.searchResultsId);
    if (results?.contains(document.activeElement)) {
      results.parentElement?.querySelector<HTMLInputElement>("input")?.focus();
    }
  }

  /** Tracks whether focus is currently within the search wrapper — see onSearchClickOutside. */
  onSearchFocusChange(focused: boolean): void {
    this.isSearchFocused = focused;
  }

  /**
   * ClickOutsideDirective's Escape listener is global to the document — an Escape press meant
   * to dismiss something else on the page (e.g. an unrelated dialog or popover) must not
   * silently wipe an in-progress typed search query. A real outside click can't have this
   * problem (its mousedown/mouseup targeting is already specific to this host), so only Escape
   * needs the extra focus-containment check. Tabbing out of the search ("focus-out") clears it
   * like an outside click, which is what closes the results dropdown.
   */
  onSearchClickOutside(reason: ClickOutsideReason = "outside-click"): void {
    if (this.searchQuery.trim().length === 0) {
      return;
    }
    if (reason === "escape" && !this.isSearchFocused) {
      return;
    }
    if (reason === "escape") {
      this.refocusSearchIfInResults();
    }
    this.onSearchChange("");
  }

  onSearchChange(value: string): void {
    // Search + compare hit the gloss-grocery backend directly and need no
    // extension — they work on web and mobile alike. The extension gate now
    // lives on the live-price refresh action only (see refreshRow).
    this.searchQuery = value;

    // Only whitespace/case changed (e.g. pressing space after a term) — the
    // effective query is identical, so don't re-fire the request or reset the
    // shown results back to a loading state.
    const normalized = normalizeQuery(value);
    if (normalized === this.lastNormalizedQuery) {
      return;
    }
    this.lastNormalizedQuery = normalized;

    if (normalized.length < SEARCH_MIN_LENGTH) {
      // Short-circuit locally so the empty state appears instantly, without
      // waiting out the debounce for input that will never be searched.
      this.searchVm.set(EMPTY_SEARCH_VM);
    } else {
      // Reset rows too — this is a new query, not a "load more" of the old
      // one, so stale results from the previous query must not linger behind
      // the loading state.
      this.searchVm.set({ loading: true, error: false, rows: [], hasMore: false, total: 0 });
    }
    // Invalidates any request still in flight for the previous query (e.g. an
    // auto-paging "show all" fetch mid-debounce) so its stale response can't
    // pass the `activeQuery !== query` guard in runSearch$ and land under the
    // state we just reset — mirrors resetSearchAfterAdd's same guard.
    this.resetSearchRequestState();
    this.queryInput$.next(normalized);
  }

  /** Fetches the next page of results for the currently active query and appends them. */
  loadMore(): void {
    if (this.loadingMore()) {
      return;
    }
    this.loadMore$.next();
  }

  /** "Show all": reveals every fetched row; the constructor effect auto-pages the rest. */
  expandSearchDropdown(): void {
    this.searchDropdownExpanded.set(true);
    // Retry after a failure: set(true) alone is a no-op when already expanded.
    this.searchLoadMoreFailed.set(false);
  }

  /**
   * Runs one search page as an Observable so the constructor's switchMap can
   * cancel it when a newer search supersedes it. Never errors the outer stream:
   * a failed request is folded into the error VM and swallowed with EMPTY, so it
   * can't tear down the long-lived search pipeline. `finalize` clears
   * `loadingMore` on completion, error, *and* cancellation — otherwise a
   * switchMap-cancelled "load more" would leave the flag stuck and dead-lock the
   * "Show more" button.
   */
  private runSearch$(query: string, offset: number): Observable<unknown> {
    this.activeQuery = query;
    this.loadingMore.set(offset > 0);
    return this.grocery.search(query, offset).pipe(
      tap((page) => {
        // Superseded by a reset or a newer search (e.g. addItem clearing the
        // query while this page was in flight) — drop it instead of reviving
        // stale rows under whatever query/state is now active.
        if (this.activeQuery !== query) {
          return;
        }
        // Advance by what the server actually returned this page, not by how
        // many of those rows survive client-side dedup — otherwise a partial
        // duplicate page (a few ids already shown) undercounts progress and
        // the next "load more" re-requests and re-filters the same items.
        this.nextSearchOffset = offset + page.rows.length;
        let newRowsAdded = page.rows.length;
        this.searchVm.update((vm) => {
          // Drop any row already on screen — a backend paging edge case (e.g.
          // the exclude set shifting between two auto-paged requests) could
          // otherwise hand back an id already appended, which would collide
          // with the template's `track row.item.id` and, since it wouldn't
          // grow `rows.length` (the next page's offset), re-request the same
          // offset forever.
          const existingIds = offset === 0 ? null : new Set(vm.rows.map((row) => row.item.id));
          const newRows = existingIds
            ? page.rows.filter((row) => !existingIds.has(row.item.id))
            : page.rows;
          newRowsAdded = newRows.length;
          // Hard-cap post-append, not just via the pre-fetch guard in the
          // auto-pager effect, so a page-size/cap mismatch can't overshoot it.
          const combined = offset === 0 ? page.rows : [...vm.rows, ...newRows];
          const rows = combined.slice(0, GlossGroceryComponent.MAX_SHOW_ALL_ROWS);
          const atCap = rows.length >= GlossGroceryComponent.MAX_SHOW_ALL_ROWS;
          // The cap slice above can itself drop fetched rows even when the
          // server says there's nothing more (page.hasMore false) — force
          // hasMore true whenever that happened, so the "capped" message
          // still explains the shortfall instead of it passing silently.
          const hasMore = page.hasMore || combined.length > rows.length;
          // Best-value membership must be recomputed over every accumulated
          // row, not just this page, so auto-paging can't leave the wrong row
          // flagged best — but only once the set has actually settled.
          // Settled means no further auto-page will fetch more rows: either
          // the server is done (!page.hasMore), or the row cap has already
          // been reached (atCap) — the auto-pager effect never fires again
          // past the cap regardless of what the server still has. Using the
          // display-only `hasMore` here (which is forced true by the cap
          // itself) would wrongly treat the very page that hits the cap as
          // "still interim" and skip the recompute it will now never get.
          // Carry the prior badge forward only for genuinely interim pages —
          // the UI shows "Loading more matches…" for that state, so the
          // interim result is never shown as final — and let the settled
          // page pay for the O(rows) grouping pass once.
          const willAutoPageAgain = page.hasMore && !atCap;
          let decoratedRows: typeof rows;
          if (offset > 0 && willAutoPageAgain) {
            const previousBestValue = new Map(vm.rows.map((row) => [row.item.id, row.isBestValue]));
            decoratedRows = rows.map((row) => ({
              ...row,
              isBestValue: previousBestValue.get(row.item.id) ?? false,
            }));
          } else {
            const bestIds = bestValueIds(rows.map((row) => row.item));
            decoratedRows = rows.map((row) => ({ ...row, isBestValue: bestIds.has(row.item.id) }));
          }
          return {
            loading: false,
            error: false,
            rows: decoratedRows,
            hasMore,
            // While more pages remain, guard against a page's total (re-derived
            // server-side on every request) dipping below what's already on
            // screen. Once nothing more will be fetched (hasMore false — no
            // stall, no cap), no future page can ever close the gap between
            // `total` and `rows.length`, so trust the rows actually shown
            // instead of a server total the UI can no longer justify (e.g. an
            // all-duplicate final page with a stale higher count).
            total: hasMore ? Math.max(page.total, rows.length) : rows.length,
          };
        });
        // A genuinely empty page that still claims more exist can't make
        // progress — nextSearchOffset above stays put, so retrying would just
        // re-request the same offset forever; treat it like a network error.
        // A page that came back non-empty but entirely duplicates already
        // shown also can't make visible progress: nothing was appended, so
        // treat it the same way rather than letting the auto-pager effect
        // fire again immediately with no backoff — nextSearchOffset still
        // advances underneath, so a manual retry (which re-enables the
        // effect) resumes from the new offset rather than re-requesting the
        // same one.
        const stalled =
          offset > 0 && page.hasMore && (page.rows.length === 0 || newRowsAdded === 0);
        this.searchLoadMoreFailed.set(stalled);
        // Always re-derive from the just-updated rows: a "load more" page's
        // cap-slice can drop previously-visible rows too, and any refreshState
        // entry for an id no longer shown must not linger just because this
        // wasn't the first page.
        this.pruneRefreshState();
      }),
      catchError(() => {
        // Same guard as tap() above: a request superseded by a reset (e.g.
        // addItem clearing the query while this page was in flight) must not
        // clobber whatever query/state is active now with this stale error.
        if (this.activeQuery !== query) {
          return EMPTY;
        }
        // offset 0: surface the error state. offset > 0: keep the existing
        // page and `hasMore` so "show all" can resume; searchLoadMoreFailed
        // stops the auto-pager from retrying the same page forever.
        if (offset === 0) {
          this.searchVm.set({ loading: false, error: true, rows: [], hasMore: false, total: 0 });
          // Clear the dedup key alongside the state reset (as addItem does), so
          // retyping the identical term re-runs the search instead of being
          // swallowed as "unchanged" — the only way out otherwise (no retry
          // button) is to alter the query text. Reset to null, not "", so
          // clearing the box also dismisses this error (see the field doc).
          this.lastNormalizedQuery = null;
        } else {
          // A failed "load more" keeps the page the user already has; the active
          // query is unchanged, so the dedup key must stay put.
          this.searchVm.update((vm) => ({ ...vm, loading: false }));
          this.searchLoadMoreFailed.set(true);
        }
        return EMPTY;
      }),
      finalize(() => {
        this.loadingMore.set(false);
      }),
    );
  }

  /** Round-trips one price request through the extension's content-script bridge (postMessage). */
  private fetchViaExtension(url: string, timeoutMs: number): Promise<ExtensionFetchResult> {
    return new Promise<ExtensionFetchResult>((resolve, reject) => {
      const origin = window.location.origin;
      const reqId =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;

      const timer = setTimeout(() => {
        window.removeEventListener("message", onMessage);
        reject(new Error("extension-timeout"));
      }, timeoutMs);

      const onMessage = (event: MessageEvent): void => {
        if (event.source !== window || event.origin !== origin) {
          return;
        }
        const data = event.data as {
          __gloss?: boolean;
          type?: string;
          reqId?: string;
          result?: ExtensionFetchResult;
        };
        if (data?.__gloss !== true || data.reqId !== reqId) {
          return;
        }
        if (data.type === "GLOSS_FETCH_PRICE_RESULT") {
          clearTimeout(timer);
          window.removeEventListener("message", onMessage);
          resolve(data.result ?? { ok: false, error: "empty result" });
        }
      };

      window.addEventListener("message", onMessage);
      window.postMessage({ __gloss: true, type: "GLOSS_FETCH_PRICE", reqId, url }, origin);
    });
  }

  /** Web live-price fetch: one extension round-trip, reduced to a price (or null). */
  private async fetchPriceViaExtension(url: string): Promise<number | null> {
    const r = await this.fetchViaExtension(url, GlossGroceryComponent.REFRESH_TIMEOUT_MS);
    return r.ok && r.price != null ? r.price : null;
  }

  /**
   * Mobile live-price fetch: pull the store page over Capacitor's native HTTP
   * (no CORS, device IP — the extension-less path) and extract a price. Returns
   * null if the page couldn't be read or carried no price (e.g. a bot-wall
   * challenge). First-cut extraction (see extractLivePrice) — refine per store
   * once verified on a real device.
   */
  private async fetchPriceViaCapacitor(url: string): Promise<number | null> {
    const res = await capFetch<unknown>({
      url,
      method: "GET",
      headers: {
        Accept: "text/html,application/json",
        "User-Agent":
          "Mozilla/5.0 (iPhone; CPU iPhone OS 17_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.0 Mobile/15E148 Safari/604.1",
      },
    });
    if (res.status < 200 || res.status >= 300) {
      return null;
    }
    const body = typeof res.data === "string" ? res.data : JSON.stringify(res.data ?? "");
    return extractLivePrice(body);
  }

  /**
   * "Refresh prices" for one row: fetch a live price from every store that
   * carries the item (via the extension, in parallel) and merge whatever comes
   * back into the row, stamping "just now". Stores that fail to return a price
   * keep their existing value. Updates the UI only — writing the fresh price
   * back to the catalog DB is a separate, deferred job.
   *
   * `isListRow` routes the update: list rows go through the service (so totals
   * recompute); transient search-result rows are re-decorated in place.
   *
   * `skipAvailabilityCheck` lets `refreshAllPrices` pass rows through after it
   * has already gated the extension once itself, instead of every row
   * re-running `ensureLiveFetchAvailable`.
   *
   * Re-entrancy: a no-op if this item already has a `refreshRow` call in
   * flight (from a manual click racing the bulk sweep, or a double click) —
   * without this, two concurrent calls for the same item would fire duplicate
   * extension fetches and duplicate `persistPrices` POSTs.
   *
   * `showToast` fires the "Prices updated" toast for a directly refreshed list
   * row; the bulk sweep passes `false` and reports one aggregate toast instead.
   * A manual click that overlaps a bulk sweep at all is also suppressed — if it
   * finishes mid-sweep it's folded into the aggregate toast, and if it finishes
   * after the sweep ended the aggregate already fired — so one sweep never
   * produces more than one toast.
   *
   * Resolves `true` if any new price was saved to the server, `false` otherwise
   * (failed fetch, nothing saved, only applied locally because the backend was
   * unreachable, or skipped as a re-entrant call).
   */
  async refreshRow(
    row: GroceryResultRow,
    isListRow: boolean,
    skipAvailabilityCheck = false,
    showToast = true,
  ): Promise<boolean> {
    const item = row.item;
    if (this.refreshingRowIds.has(item.id)) {
      return false;
    }
    this.refreshingRowIds.add(item.id);
    // Snapshot before any fetch/persist so the toast can tell whether the price
    // actually moved, not just whether the row was touched.
    const pricesBefore = { ...(item.prices ?? {}) };
    const sweepEpochAtStart = this.sweepEpoch;
    try {
      const result = await this.doRefreshRow(row, isListRow, skipAvailabilityCheck);
      // Local-only updates already say "couldn't save to the server" in the
      // row; a green "Prices updated" toast would contradict that.
      const saved = result?.persisted === true;
      if (saved && isListRow) {
        if (this.sweepSaved) {
          // Keep the earliest snapshot if the row was saved twice in one sweep.
          if (!this.sweepSaved.has(item.id)) {
            this.sweepSaved.set(item.id, pricesBefore);
          }
        } else if (
          showToast &&
          !this.refreshingAll() &&
          // A sweep started or ended while this call was in flight.
          this.sweepEpoch === sweepEpochAtStart &&
          // Removed mid-refresh: updateItemPrices was a no-op, nothing recalculated.
          this.listRows().some((r) => r.item.id === item.id)
        ) {
          const changed = pricesChanged(pricesBefore, { ...pricesBefore, ...result.prices });
          this.showPricesUpdatedToast(changed ? 1 : 0, 1);
        }
      }
      return saved;
    } finally {
      this.refreshingRowIds.delete(item.id);
    }
  }

  /**
   * The store keys for an item whose source URL is https and pinned to that
   * store's own supermarket host — i.e. the stores actually safe to live-fetch.
   * A bad/poisoned catalog row otherwise becomes an open fetch from the user's
   * device/browser (see store-url-allowlist). Shared by the single-row refresh
   * and the bulk sweep's "extension missing" messaging so a row with no usable
   * link is treated the same in both (no refresh, no install CTA).
   */
  private allowedStoreUrls(item: GroceryItem): GroceryStoreKey[] {
    const urls = item.sourceUrls ?? {};
    return (Object.keys(urls) as GroceryStoreKey[]).filter(
      (s) => urls[s] && isAllowedStoreUrl(s, urls[s] as string),
    );
  }

  /**
   * Returns the prices applied to the row and whether the server saved them
   * (`persisted: false` = shown locally only), or `null` if none were applied.
   */
  private async doRefreshRow(
    row: GroceryResultRow,
    isListRow: boolean,
    skipAvailabilityCheck: boolean,
  ): Promise<{ prices: Partial<Record<GroceryStoreKey, number>>; persisted: boolean } | null> {
    const item = row.item;
    const urls = item.sourceUrls ?? {};
    const stores = this.allowedStoreUrls(item);
    if (stores.length === 0) {
      this.setRefresh(item.id, {
        loading: false,
        error: true,
        message: "No store link to refresh.",
      });
      return null;
    }

    // The live fetch needs a way past the stores' CORS/bot walls. On the native
    // mobile app that's Capacitor's native HTTP (no extension). On web it's the
    // Gloss extension — so if we're on web without it, beta users get the inline
    // "Install extension" CTA (extensionMissing) and everyone else gets the "mobile only"
    // toast, rather than firing a call that cannot succeed.
    const mobile = this.isMobileApp;
    if (!skipAvailabilityCheck && !(await this.ensureLiveFetchAvailable())) {
      if (this.roleAccessService.isBetaUser()) {
        this.setRefresh(item.id, {
          loading: false,
          error: true,
          message: GlossGroceryComponent.EXTENSION_REQUIRED_MESSAGE,
          extensionMissing: true,
        });
      } else {
        this.showMobileOnlyToast();
      }
      return null;
    }

    this.setRefresh(item.id, {
      loading: true,
      message: this.i18nService.t("groceryRefreshingPrices"),
    });

    // 1. Fetch a live price from each store, in parallel — natively on mobile,
    //    via the extension on web.
    const results = await Promise.all(
      stores.map(async (store) => {
        try {
          const price = mobile
            ? await this.fetchPriceViaCapacitor(urls[store] as string)
            : await this.fetchPriceViaExtension(urls[store] as string);
          return { store, price, timedOut: false };
        } catch {
          return { store, price: null, timedOut: true }; // fetch never returned
        }
      }),
    );

    const fetched: Partial<Record<GroceryStoreKey, number>> = {};
    for (const r of results) {
      if (r.price != null) {
        fetched[r.store] = r.price;
      }
    }
    const fetchedCount = Object.keys(fetched).length;
    const plural = (n: number): string => (n === 1 ? "store" : "stores");

    if (fetchedCount === 0) {
      const allFailed = results.every((r) => r.timedOut);
      // Every store timed out on web is ambiguous: the extension may be gone, or
      // just slow/busy (or a store page hung behind a bot wall). The gate waved us
      // through on a stale `installed` flag (check() never flips a true back to
      // false on a missed ping), so actively re-ping before telling the user to
      // install — only an unanswered ping proves it's actually absent (#5942). A
      // present-but-slow extension falls through to the generic store-blocked copy
      // with no install CTA.
      const extensionAbsent = allFailed && !mobile && !(await this.extensionService.check());
      // Same split as the gate: only beta users are pointed at the extension. Others are told
      // refresh is mobile-only, inline, since a sweep can hit this on many rows at once and a
      // toast per row would spam (the gate's single up-front toast can't cover this late miss).
      const offerInstall = extensionAbsent && this.roleAccessService.isBetaUser();
      let message = "Couldn't read a fresh price (the store may have blocked the fetch).";
      if (offerInstall) {
        message = "Gloss extension not detected — install it to fetch live prices.";
      } else if (extensionAbsent) {
        message = this.i18nService.t("groceryRefreshMobileOnlyToastBody");
      }
      this.setRefresh(item.id, {
        loading: false,
        error: true,
        message,
        // Only the confirmed-absent beta case gets the CTA; a store-blocked fetch, a
        // slow-but-present extension, or a non-beta user leaves the entry clean (no extensionMissing key).
        ...(offerInstall ? { extensionMissing: true } : {}),
      });
      return null;
    }

    // 2. Persist to the shared catalog and reconcile the UI with what the
    //    server actually SAVED (it validates each price server-side).
    try {
      const res = await firstValueFrom(this.grocery.persistPrices(item.id, fetched));
      const prices: Partial<Record<GroceryStoreKey, number>> = {};
      const priceAsOf: Partial<Record<GroceryStoreKey, string>> = {};
      for (const [store, applied] of Object.entries(res.applied) as [
        GroceryStoreKey,
        { price: number; scrapedAt: string },
      ][]) {
        prices[store] = applied.price;
        priceAsOf[store] = applied.scrapedAt;
      }
      const savedCount = Object.keys(prices).length;
      if (savedCount > 0) {
        this.applyPriceUpdate(item, prices, priceAsOf, isListRow);
      }
      const rejectedCount = Object.keys(res.rejected).length;
      this.setRefresh(item.id, {
        loading: false,
        error: savedCount === 0,
        message:
          savedCount > 0
            ? `Saved ${savedCount} of ${fetchedCount} ${plural(fetchedCount)} just now.` +
              (rejectedCount > 0 ? ` ${rejectedCount} not saved (price looked off).` : "")
            : "Fetched prices weren't saved — they looked off versus the known price.",
      });
      return savedCount > 0 ? { prices, persisted: true } : null;
    } catch {
      // 3. Backend unreachable — show the fetched price locally but be honest
      //    that it wasn't persisted.
      const now = new Date().toISOString();
      const priceAsOf: Partial<Record<GroceryStoreKey, string>> = {};
      for (const store of Object.keys(fetched) as GroceryStoreKey[]) {
        priceAsOf[store] = now;
      }
      this.applyPriceUpdate(item, fetched, priceAsOf, isListRow);
      this.setRefresh(item.id, {
        loading: false,
        error: false,
        message: `Updated ${fetchedCount} ${plural(fetchedCount)} locally — couldn't save to the server.`,
      });
      return { prices: fetched, persisted: false };
    }
  }

  /**
   * True if the live-price fetch path is usable right now: always true on the
   * native mobile app (no extension needed there); otherwise re-checks the
   * extension unless it's already known to be installed. Shared by refreshRow
   * and refreshAllPrices so the gating condition can't drift between the
   * single-row and bulk call sites.
   *
   * A pure check — it never navigates. A miss on web is surfaced inline on the
   * row/card (an "Install extension" CTA, see EXTENSION_REQUIRED_MESSAGE +
   * extensionMissing) rather than redirecting the user mid-action, so the error
   * message is actually painted and a navigation can't supersede a route the
   * user kicked off while the ~1.5s check was still resolving (#5942, #5695).
   * Non-beta users get the "mobile only" toast instead of the CTA; see the call sites.
   */
  private async ensureLiveFetchAvailable(): Promise<boolean> {
    if (this.isMobileApp || this.extensionInstalled()) {
      return true;
    }
    return this.extensionService.check();
  }

  /**
   * Price Comparison card's "Refresh Price" action: refreshes every list row,
   * up to `REFRESH_ALL_CONCURRENCY` at a time, via refreshRow. The extension is
   * gated once, up front, here, and `refreshRow` is told to skip its own gate
   * (`skipAvailabilityCheck`) — so a missing extension means exactly one check,
   * then one inline "Install extension" CTA per refreshable row, not a redirect
   * (#5942). `refreshingAll` is set before the
   * (possibly ~1.5s) extension check awaits, not after, so a second click
   * during that window is blocked by the guard above instead of starting a
   * concurrent sweep. The list itself is re-read only after that check
   * resolves, so it reflects any edits the user made while it was pending.
   */
  async refreshAllPrices(): Promise<void> {
    if (this.refreshingAll() || !this.hasRefreshableRows()) {
      return;
    }
    this.refreshingAll.set(true);
    const sweepSaved = new Map<string, Partial<Record<GroceryStoreKey, number>>>();
    this.sweepSaved = sweepSaved;
    this.sweepEpoch++;
    try {
      if (!(await this.ensureLiveFetchAvailable())) {
        // No extension on web. Non-beta users get one "mobile only" toast for the whole sweep.
        if (!this.roleAccessService.isBetaUser()) {
          this.showMobileOnlyToast();
          return;
        }
        // No extension on web: mirror refreshRow's single-item messaging across
        // every currently visible row — an error with an inline "Install
        // extension" CTA. The gate no longer navigates away, so these messages
        // are actually painted now (#5942). Skip rows the single-row path would
        // skip too: custom rows and rows with no allowed store link — installing
        // the extension wouldn't let those refresh, so don't offer the CTA there.
        for (const row of this.listRows()) {
          if (row.isCustom || this.allowedStoreUrls(row.item).length === 0) {
            continue;
          }
          this.setRefresh(row.item.id, {
            loading: false,
            error: true,
            message: GlossGroceryComponent.EXTENSION_REQUIRED_MESSAGE,
            extensionMissing: true,
          });
        }
        return;
      }
      // Custom items have no source URLs, so they're excluded from the
      // per-row refresh menu (see `!row.isCustom` in the template) — skip
      // them here too instead of spamming every one with a "no store link"
      // refreshState error on each bulk refresh.
      const rows = this.listRows().filter((row) => !row.isCustom);
      if (rows.length === 0) {
        return;
      }
      await lastValueFrom(
        from(rows).pipe(
          mergeMap(
            // Isolate one row's failure from the rest of the sweep — refreshRow
            // already reports its own errors via refreshState, so there's
            // nothing more to do with a rejection here than stop it from
            // aborting the other in-flight/queued rows. showToast: false since
            // this sweep reports one aggregate toast below, not one per row;
            // saved rows are recorded into sweepSaved by refreshRow itself.
            (row) => this.refreshRow(row, true, true, false).catch(() => undefined),
            GlossGroceryComponent.REFRESH_ALL_CONCURRENCY,
          ),
        ),
        { defaultValue: undefined },
      );
      // No row saved a price: each already shows its own error (or its
      // local-only notice), so a green "Prices updated" toast would misreport
      // the sweep as a success.
      // Count only rows that actually saved — failed/skipped rows weren't
      // checked, so reporting them as "unchanged" (N of rows.length) would
      // overstate what the sweep did. Rows removed mid-sweep are skipped too.
      const itemsAfter = new Map(this.listRows().map((row) => [row.item.id, row.item]));
      let changedCount = 0;
      let totalCount = 0;
      for (const [id, before] of sweepSaved) {
        const after = itemsAfter.get(id);
        if (!after) {
          continue;
        }
        totalCount++;
        if (pricesChanged(before, after.prices ?? {})) {
          changedCount++;
        }
      }
      if (totalCount === 0) {
        return;
      }
      // A sweep the user starts and then navigates away from keeps running and
      // fires this global toast on whatever page they're now on. Kept on purpose
      // (#5942): the work genuinely completed, and the toast is app-global rather
      // than tied to this page, so it reads as a true "your prices are updated"
      // confirmation wherever they landed.
      this.showPricesUpdatedToast(changedCount, totalCount);
    } finally {
      this.refreshingAll.set(false);
      this.sweepSaved = undefined;
      this.sweepEpoch++;
      // A row can be removed from the list mid-sweep; its refreshRow call
      // still resolves and writes a result into refreshState, resurrecting an
      // entry for an id that's no longer visible. Prune once the whole sweep
      // settles rather than leaving it for the next unrelated list change.
      this.pruneRefreshState();
    }
  }

  /**
   * Shared options for the green checkmark "guide success" toast. Used by both
   * showPricesUpdatedToast and deletePastList's "list deleted" toast.
   */
  private readonly guideSuccessToastOptions = {
    extraClass: "toast-guide-success",
    toastComponent: GuideSuccessToastComponent,
  };

  /**
   * "Prices updated" toast shown after a bulk "Refresh Price" sweep, or after a
   * single list row's refresh (as "N of 1"). Reuses the
   * guide-success toast look (green, checkmark icon, title + body) rather than
   * a plain toast, since this is a positive confirmation, not just a status line.
   */
  private showPricesUpdatedToast(changedCount: number, totalCount: number): void {
    this.globalService.showMessage(
      "success",
      this.i18nService.t("groceryPricesUpdatedToastTitle"),
      changedCount === 0
        ? this.i18nService.t("groceryPricesUpdatedToastBodyNoChange")
        : totalCount === 1
          ? this.i18nService.t("groceryPricesUpdatedToastBodySingle")
          : this.i18nService.t("groceryPricesUpdatedToastBody", changedCount, totalCount),
      false,
      this.guideSuccessToastOptions,
    );
  }

  /**
   * Applies merged prices/timestamps to the right place: list rows go through
   * the service (so totals recompute); transient search-result rows are
   * re-decorated in place in the component's own state.
   */
  private applyPriceUpdate(
    item: GroceryItem,
    prices: Partial<Record<GroceryStoreKey, number>>,
    priceAsOf: Partial<Record<GroceryStoreKey, string>>,
    isListRow: boolean,
  ): void {
    if (isListRow) {
      this.grocery.updateItemPrices(item.id, prices, priceAsOf);
      return;
    }
    const rows = this.searchVm().rows;
    const items = rows.map((r) =>
      r.item.id === item.id
        ? {
            ...item,
            prices: { ...item.prices, ...prices },
            priceAsOf: { ...(item.priceAsOf ?? {}), ...priceAsOf },
          }
        : r.item,
    );
    // Best-value membership depends on every row sharing this item's
    // category/unit group, so it must be recomputed over the full set — but
    // chips/unit price only actually changed for the refreshed row. Rebuild
    // that one row and just patch isBestValue on the rest, instead of
    // re-decorating up to MAX_SHOW_ALL_ROWS rows for a single-item update.
    const bestIds = bestValueIds(items);
    this.searchVm.update((vm) => ({
      ...vm,
      rows: rows.map((row, i) =>
        row.item.id === item.id
          ? { ...buildResultRow(items[i]), isBestValue: bestIds.has(item.id) }
          : { ...row, isBestValue: bestIds.has(row.item.id) },
      ),
    }));
  }

  /** Writes one item's refresh state into the keyed `refreshState` map. */
  private setRefresh(id: string, value: RefreshEntry): void {
    this.refreshState.update((m) => ({ ...m, [id]: value }));
  }

  /**
   * Drops refresh state for items that are no longer shown — neither on the list
   * nor in the current search results. Keeps the map bounded and stops a stale
   * per-item message from resurfacing if the same id turns up in a later search.
   */
  private pruneRefreshState(): void {
    const visible = new Set<string>(this.listItemIds);
    for (const row of this.searchVm().rows) {
      visible.add(row.item.id);
    }
    this.refreshState.update((m) => {
      const next: Record<string, RefreshEntry> = {};
      for (const id of Object.keys(m)) {
        if (visible.has(id)) {
          next[id] = m[id];
        }
      }
      return next;
    });
  }

  addItem(item: GroceryItem): void {
    // If the active list hasn't loaded yet the add is dropped; keep the search
    // context so the tap isn't silently lost (the user can add once it loads).
    if (!this.grocery.addItem(item)) {
      return;
    }
    this.resetSearchAfterAdd();
  }

  /** Add whatever's typed as a custom/unlisted item (no price) — for items not in the catalog. */
  addCustomFromSearch(): void {
    // Dropped when the name is blank, or the active list hasn't loaded / failed
    // to load; keep the search context so the tap isn't silently lost (as addItem does).
    if (!this.grocery.addCustomItem(this.searchQuery)) {
      return;
    }
    this.resetSearchAfterAdd();
  }

  /**
   * Asks the team to source a price for a custom/unlisted item. Reports the term
   * to the backlog and shows a per-item confirmation only once it's actually
   * recorded; a failed report is swallowed (never surfaces an error) and leaves
   * the button so the user can retry.
   */
  requestPrice(row: ShoppingRow): void {
    this.grocery.requestPrice(row.item.name).subscribe((reported) => {
      // Only confirm once the term is actually recorded. On failure the service
      // has re-armed the term and we leave the button in place so the user can
      // retry, rather than showing a confirmation for something we dropped.
      if (reported) {
        this.priceRequested.update((s) => new Set(s).add(row.item.id));
      }
    });
  }

  /** Clears the search UI after an item is added (shared by catalog + custom adds). */
  private resetSearchAfterAdd(): void {
    // An add from a result row (keyboard or click) closes the dialog it's in.
    this.refocusSearchIfInResults();
    this.searchQuery = "";
    // Reset the dedup key so retyping the same term after adding an item
    // re-runs the search instead of being swallowed as "unchanged".
    this.lastNormalizedQuery = null;
    this.searchVm.set(EMPTY_SEARCH_VM);
    this.resetSearchRequestState();
  }

  /**
   * Invalidates any request in flight for the previous query (so its stale
   * response can't land under the state we're about to reset), collapses the
   * "show all" dropdown UI back to the top-N preview, clears any auto-pager
   * failure, and prunes now-stale refresh state — shared by a fresh query
   * (onSearchChange) and post-add cleanup (resetSearchAfterAdd), which both
   * need exactly this sequence.
   */
  private resetSearchRequestState(): void {
    this.activeQuery = "";
    this.nextSearchOffset = 0;
    this.searchDropdownExpanded.set(false);
    this.searchLoadMoreFailed.set(false);
    // The old result rows are gone — prune their refresh state now rather than
    // waiting for a debounced search to land (which may never come, e.g. a
    // sub-2-char query).
    this.pruneRefreshState();
  }

  removeItem(listItemId: string): void {
    this.grocery.removeItem(listItemId);
    // The removed item was snapshotted into the paged exclude set, so it's
    // missing from the results still on screen. Reset the dedup key (as addItem
    // does) so re-searching the same term re-runs the query and can bring the
    // removed item back, instead of being swallowed as "unchanged". Reset to
    // null, not "", so clearing the box also drops the stale results (field doc).
    this.lastNormalizedQuery = null;
  }

  /** DOM id of a given store's "missing items" popover — shared between the trigger's
   * aria-controls and the panel itself, and used to look the panel up after it renders
   * (see toggleMissing: this markup repeats per store, so it can't use @ViewChild). */
  private static missingPanelId(store: GroceryStoreKey): string {
    return `grocery-missing-panel-${store}`;
  }

  /** Row menu's "Archive this item" — moves one item off the active list and into Archived Lists. */
  archiveItem(row: ShoppingRow): void {
    const id = row.listItemId;
    // Not while the whole list is archiving: the backend's removeItem doesn't
    // check list status, so the remove would succeed on the just-archived list
    // and leave the item both archived and removed from a list that is gone.
    if (this.archivingItems().has(id) || this.archiving()) {
      return;
    }
    this.archivingItems.update((ids) => new Set(ids).add(id));
    this.grocery
      .archiveItem(id)
      .pipe(
        finalize(() =>
          this.archivingItems.update((ids) => {
            const next = new Set(ids);
            next.delete(id);
            return next;
          }),
        ),
      )
      .subscribe({
        next: () => {
          // Same reason as removeItem: the item left the list, so let a
          // re-search of the same term bring it back.
          this.lastNormalizedQuery = null;
          // Surface the just-archived item if Archived Lists is open.
          if (this.showPastLists()) {
            this.loadPastLists$.next();
          }
        },
        error: (e: unknown) => {
          // eslint-disable-next-line no-console
          console.error("Failed to archive item", e);
          this.platformUtils.showToast("error", "", this.i18nService.t("groceryArchiveItemFailed"));
        },
      });
  }

  /** Tick an item off (or un-tick it) — "I bought this". */
  togglePurchased(row: ShoppingRow): void {
    this.grocery.setPurchased(row.listItemId, !row.purchased);
  }

  /** Open the styled dialog to rename the active list (a nickname so it's identifiable in Past lists). */
  renameList(): void {
    const list = this.activeList();
    if (!list) {
      return;
    }
    const data: DialogWrapperData = {
      title: "groceryRenameList",
      showBackButton: false,
      component: RenameListDialogComponent,
      componentInputs: {
        currentName: list.name ?? "",
        closeDialog: () => this.renameDialogRef?.close(),
        onRename: (name: string) =>
          this.grocery.renameActive(name).pipe(
            tap({
              // eslint-disable-next-line no-console
              error: (e: unknown) => console.error("Failed to rename list", e),
            }),
          ),
      },
    };
    this.renameDialogRef = this.rightPanel.open(DialogWrapperComponent, {
      data,
    });
  }

  /**
   * Archives the active list. If every item is ticked off there's nothing to
   * lose, so this archives right away; otherwise it confirms first — "Archive
   * this list?" — naming the still-unticked items and offering to carry them
   * into a fresh list (requirement #8) via {@link performArchive}. Dismissing
   * the confirm (close X, Esc, backdrop) cancels without archiving.
   */
  async archiveList(): Promise<void> {
    const list = this.activeList();
    // A second tap while one archive is in flight would PATCH the same list
    // again and create a second follow-on list. Also wait out any "Archive this
    // item" — its remove targets this list's id.
    if (!list || this.archiving() || this.archivingItems().size > 0) {
      return;
    }
    this.archiving.set(true);
    try {
      const unticked = (list.items ?? []).filter((i) => !i.purchased);
      if (unticked.length === 0) {
        await this.performArchive(false);
        return;
      }
      const carryForward = await this.confirmation.choiceFor(
        ConfirmationEnum.archivingUntickedGroceryList,
        {
          messageReplacement: this.i18nService.t(
            unticked.length === 1
              ? "groceryArchiveUntickedConfirmMessageOne"
              : "groceryArchiveUntickedConfirmMessage",
            String(unticked.length),
            this.joinNames(unticked.map((i) => i.name)),
          ),
        },
      );
      if (carryForward === null) {
        return;
      }
      await this.performArchive(carryForward);
    } finally {
      this.archiving.set(false);
    }
  }

  /**
   * "A, B and C" (or "A, B, C and 4 more" past {@link ARCHIVE_CONFIRM_MAX_NAMES})
   * for the archive-confirm message. Names are HTML-escaped because the dialog
   * renders a generated message as sanitized HTML, which would otherwise strip
   * or mangle a name like "<3 cookies".
   */
  private joinNames(names: string[]): string {
    // "and 1 more" is no shorter than the name itself, so show it instead.
    const limit =
      names.length > ARCHIVE_CONFIRM_MAX_NAMES + 1 ? ARCHIVE_CONFIRM_MAX_NAMES : names.length;
    const shown = names.slice(0, limit).map((n) => Utils.escapeHtml(n));
    const hidden = names.length - shown.length;
    if (hidden > 0) {
      shown.push(this.i18nService.t("groceryAndNMore", String(hidden)));
    }
    if (shown.length <= 1) {
      return shown.join("");
    }
    return this.i18nService.t(
      "groceryNamesAndLast",
      shown.slice(0, -1).join(this.i18nService.t("groceryNamesSeparator")),
      shown[shown.length - 1],
    );
  }

  /** Archives the active list and starts the follow-on list, carrying forward un-ticked items if asked to. */
  private async performArchive(carryForward: boolean): Promise<void> {
    try {
      await firstValueFrom(this.grocery.archiveActiveAndStartNew(carryForward));
      // New list is a fresh start — a store tab (and any locked default) picked
      // for the old list may not apply to it (e.g. "sold at Coles" for items
      // that just got carried over).
      this.selectedStore.set("best");
      this.defaultStore.set(null);
      // Archive persisted and the new list is ready — if Past lists is open,
      // surface the just-archived list without a hide/re-show.
      if (this.showPastLists()) {
        this.loadPastLists$.next();
      }
    } catch (e) {
      // Surface the failure — otherwise a failed archive/carry-forward leaves
      // the user with no list and no idea the un-ticked items were stranded.
      // eslint-disable-next-line no-console
      console.error("Failed to archive list", e);
      const archived = this.activeList() === null;
      this.platformUtils.showToast(
        "error",
        "",
        this.i18nService.t(archived ? "groceryArchivedNewListFailed" : "groceryArchiveFailed"),
      );
      if (archived) {
        // Archive already persisted (list gone), only the follow-on create
        // failed: refresh an open Past lists section so the archived (un-ticked)
        // items are recoverable there, and reset the store tab (and any locked
        // default) — whatever list loads next is unrelated to the stale one.
        this.selectedStore.set("best");
        this.defaultStore.set(null);
        if (this.showPastLists()) {
          this.loadPastLists$.next();
        }
      }
    }
  }

  /** Swap between the current list and the Archived Lists card, loading archived lists on open. */
  togglePastLists(): void {
    const next = !this.showPastLists();
    this.showPastLists.set(next);
    if (next) {
      this.mobileArchivedListsExpanded.set(true);
      this.loadPastLists$.next();
    }
    // The two cards swap in place, destroying the button that was just clicked, so
    // move focus into the card that replaced it rather than dropping it on <body>.
    // detectChanges() renders the swap synchronously so the lookup finds it (see
    // toggleMissing for why a microtask is too early).
    this.cdr.detectChanges();
    // On mobile the in-card toggle is hidden (display:none, can't take focus) in favour of the
    // page-header one, so fall through to whichever candidate is actually rendered.
    const targetIds = next
      ? ["grocery-archived-heading"]
      : ["grocery-archived-toggle", "grocery-archived-header-toggle"];
    targetIds
      .map((id) => document.getElementById(id))
      .find((el) => el != null && el.getClientRects().length > 0)
      ?.focus({ preventScroll: true });
  }

  /** Expand/collapse one past list to reveal its items. */
  togglePastListItems(list: ShoppingList): void {
    this.expandedPastListId.update((cur) => (cur === list._id ? null : list._id));
  }

  /**
   * "Sat 22 Aug" — the date an archived list was archived (falling back to when it was
   * created), with the year when it isn't this year and, if `withTime`, the time of day
   * (used to tell apart two lists archived on the same day).
   */
  pastListDateLabel(list: ShoppingList, withTime = false): string {
    const iso = list.archivedAt ?? list.createdAt;
    const date = iso ? new Date(iso) : null;
    if (!date || Number.isNaN(date.getTime())) {
      return list.name || "";
    }
    return date.toLocaleString(this.localeId, {
      weekday: "short",
      day: "numeric",
      month: "short",
      ...(date.getFullYear() !== new Date().getFullYear() && { year: "numeric" }),
      ...(withTime && { hour: "numeric", minute: "2-digit" }),
    });
  }

  /**
   * Sum of each item's cheapest snapshot price — an estimate of the archived list's
   * total. Null when no item carries a price, so the row shows "—" instead of "$0.00".
   */
  pastListTotal(list: ShoppingList): number | null {
    let total: number | null = null;
    for (const item of list.items) {
      const store = cheapestStore(item.prices ?? {});
      if (store) {
        total = (total ?? 0) + ((item.prices ?? {})[store] as number);
      }
    }
    return total;
  }

  /**
   * Cheapest store chip for a past-list item's snapshot prices, or null if it was
   * never priced. Uses isHighlighted, not isCheapest — isCheapest is false for an
   * item priced at only one store, which would hide its lone price.
   */
  pastItemCheapest(item: ShoppingListItem): StorePriceChip | null {
    return this.pastItemCheapestById().get(item._id) ?? null;
  }

  /**
   * "priced N ago" for a past-list item's snapshot price, so an archived price
   * isn't mistaken for today's; null when the chip carries no timestamp.
   */
  pastItemPriceAge(chip: StorePriceChip): string | null {
    return relativeTime(chip.priceAsOf) || null;
  }

  /**
   * Whether a past-list item is already on the current list, so "Add" is shown
   * as done instead of silently no-op'ing (or duplicating a custom item).
   */
  pastItemOnList(item: ShoppingListItem): boolean {
    return this.activeItemKeys().has(listItemDedupKey(item));
  }

  /**
   * The archived list's items "Add all" will add — deduped against the current
   * list and within the archived list itself. {@link copyPastList} copies exactly
   * these, so the button count always matches the result.
   */
  pastListAddableItems(list: ShoppingList): ShoppingListItem[] {
    return this.addableByPastListId().get(list._id) ?? [];
  }

  /** Copy an archived list's items into the current list (requirement #9). */
  copyPastList(list: ShoppingList): void {
    // Ignore a second click while the first copy is still running — the dedup
    // set is a snapshot taken at click time, so two in-flight copies would both
    // re-add the same custom items.
    const items = this.pastListAddableItems(list);
    if (this.pastCopyBusy() || this.pastCopyUnavailable() || items.length === 0) {
      return;
    }
    const before = this.activeList();
    this.copyingPastListId.set(list._id);
    this.grocery
      .copyListIntoActive({ ...list, items })
      .pipe(finalize(() => this.copyingPastListId.set(null)))
      .subscribe({
        next: (updated) => this.highlightAddedItems(before, updated),
        error: (e: unknown) => {
          // Surface the failure — a silent error is especially confusing here,
          // since a mid-sequence failure leaves some copied items on the list
          // and the rest missing with no hint to retry.
          // eslint-disable-next-line no-console
          console.error("Failed to copy list", e);
          this.platformUtils.showToast("error", "", this.i18nService.t("groceryCopyFailed"));
        },
      });
  }

  /**
   * Briefly outlines the Current List rows that `updated` gained over `before` and scrolls the
   * first new row into view so the user sees where the item went.
   */
  private highlightAddedItems(before: ShoppingList | null, updated: ShoppingList): void {
    const known = new Set((before?.items ?? []).map((i) => i._id));
    const added = updated.items.filter((i) => !known.has(i._id)).map((i) => i._id);
    if (added.length === 0) {
      return;
    }
    // Accumulate (not replace) so a second add inside the window keeps the first row outlined;
    // the single timer below clears them all together.
    this.highlightedListItemIds.update((ids) => new Set([...ids, ...added]));
    if (this.highlightTimeoutId) {
      clearTimeout(this.highlightTimeoutId);
    }
    this.highlightTimeoutId = setTimeout(() => this.highlightedListItemIds.set(new Set()), 5000);
    // Wait a tick so the new row has rendered.
    setTimeout(() => {
      if (this.destroyed) {
        return;
      }
      document
        .querySelector(`[data-current-list-item-id="${CSS.escape(added[0])}"]`)
        ?.scrollIntoView?.({
          block: "nearest",
          behavior: prefersReducedMotion() ? "auto" : "smooth",
        });
    });
  }

  /** Add a single archived-list item back onto the current list. */
  addPastItem(item: ShoppingListItem): void {
    // Ignore clicks while any copy-back is in flight (see addingPastItem).
    if (this.pastCopyBusy() || this.pastCopyUnavailable() || this.pastItemOnList(item)) {
      return;
    }
    const before = this.activeList();
    this.addingPastItem.set(true);
    this.grocery
      .copyItemIntoActive(item)
      .pipe(finalize(() => this.addingPastItem.set(false)))
      .subscribe({
        next: (updated) => {
          // The service hands back the unchanged list when the item was already
          // on it — nothing was added, so don't claim success.
          if (updated === before) {
            return;
          }
          this.highlightAddedItems(before, updated);
          this.platformUtils.showToast(
            "success",
            "",
            this.i18nService.t("groceryItemAddedToList", item.name),
          );
        },
        error: (e: unknown) => {
          // eslint-disable-next-line no-console
          console.error("Failed to add item from past list", e);
          this.platformUtils.showToast("error", "", this.i18nService.t("groceryAddItemFailed"));
        },
      });
  }

  /** Delete an entire past (archived) list, behind a styled confirmation dialog. */
  async deletePastList(list: ShoppingList): Promise<void> {
    // Arm the lock *before* the dialog, not after it resolves — otherwise a
    // double-click (or Delete on another row while this dialog is open) stacks
    // two confirms, then two DELETEs, the second 404-ing on an already-removed
    // list and toasting a spurious failure. Released on cancel below and via
    // `finalize` once the request settles.
    if (this.deletingPastListId() !== null) {
      return;
    }
    this.deletingPastListId.set(list._id);
    const dateLabel =
      this.pastListRows().find((row) => row.list._id === list._id)?.label ??
      this.pastListDateLabel(list);
    const itemCount = list.items.length;
    const confirmed = await this.confirmation.confirmFor(ConfirmationEnum.deletingGroceryList, {
      titleReplacement: this.i18nService.t("groceryDeleteListConfirmTitle", dateLabel),
      messageReplacement:
        itemCount === 0
          ? this.i18nService.t("groceryDeleteEmptyListConfirmMessage")
          : this.i18nService.t("groceryDeleteListConfirmMessage", String(itemCount)),
    });
    if (!confirmed) {
      this.deletingPastListId.set(null);
      return;
    }
    this.grocery
      .deleteArchivedList(list._id)
      .pipe(finalize(() => this.deletingPastListId.set(null)))
      .subscribe({
        next: () => {
          this.pastLists.update((lists) => lists.filter((l) => l._id !== list._id));
          // Drop the stale expanded id if the open list was the one deleted.
          this.expandedPastListId.update((cur) => (cur === list._id ? null : cur));
          this.globalService.showMessage(
            "success",
            this.i18nService.t("groceryArchivedListDeletedToastTitle"),
            itemCount === 0
              ? this.i18nService.t("groceryArchivedEmptyListDeletedToastBody", dateLabel)
              : this.i18nService.t(
                  "groceryArchivedListDeletedToastBody",
                  dateLabel,
                  String(itemCount),
                ),
            false,
            this.guideSuccessToastOptions,
          );
        },
        error: (e: unknown) => {
          // Surface the failure — the user just confirmed a permanent delete,
          // so a silent no-op leaves the list sitting in Past lists with no hint.
          // eslint-disable-next-line no-console
          console.error("Failed to delete list", e);
          this.platformUtils.showToast("error", "", this.i18nService.t("groceryDeleteFailed"));
        },
      });
  }

  /** Joins labels into "a, b and c" (or the equivalent with a localized joiner word). */
  private static joinLabels(labels: string[], joiner: string): string {
    if (labels.length <= 1) {
      return labels.join("");
    }
    return `${labels.slice(0, -1).join(", ")} ${joiner} ${labels[labels.length - 1]}`;
  }

  toggleMissing(store: GroceryStoreKey, trigger: HTMLElement): void {
    if (this.openPopover() === store) {
      this.closePopover(true, trigger);
      return;
    }
    this.openPopover.set(store);
    // Keyboard/screen-reader users have no other way to reach the popover than this trigger,
    // so opening it must move focus in. detectChanges() (not @ViewChild — this markup repeats
    // per store) forces the *ngIf-gated panel into the DOM synchronously, so the lookup by its
    // per-store id finds it immediately — a queueMicrotask callback would run before zone.js's
    // own change-detection tick (scheduled on onMicrotaskEmpty) and find nothing.
    const panelId = GlossGroceryComponent.missingPanelId(store);
    this.cdr.detectChanges();
    document.getElementById(panelId)?.focus({ preventScroll: true });
  }

  /**
   * Centralises every dismissal path (outside click, Escape, re-clicking the open trigger).
   * restoreFocus defaults to false (an outside click or a stray Escape shouldn't yank focus);
   * callers that know focus should return to the trigger (re-click, Escape while this popover's
   * own trigger/panel is focused) pass true.
   */
  closePopover(restoreFocus = false, trigger?: HTMLElement): void {
    this.openPopover.set(null);
    if (restoreFocus) {
      trigger?.focus();
    }
  }

  /**
   * Wired once per store tile, all reacting to the same document-global Escape keydown and the
   * same shared `openPopover` signal — guarded on this instance's own store still being the open
   * one so an Escape or outside click doesn't act twice (or steal focus from a click that landed
   * inside a *different* store's own popover).
   */
  onMissingClickOutside(
    reason: ClickOutsideReason,
    store: GroceryStoreKey,
    trigger: HTMLElement,
  ): void {
    if (this.openPopover() !== store) {
      return;
    }
    this.closePopover(reason === "escape", trigger);
  }

  /** Show/hide a list row's "…" action menu; opening moves focus to its first item. */
  toggleRowMenu(listItemId: string, trigger: HTMLElement): void {
    if (this.openRowMenu() === listItemId) {
      this.closeRowMenu(true, trigger);
      return;
    }
    this.openRowMenu.set(listItemId);
    // Same as openMissing: render the @if-gated panel synchronously so it can be focused.
    this.cdr.detectChanges();
    this.rowMenuItems(trigger)[0]?.focus();
  }

  closeRowMenu(restoreFocus = false, trigger?: HTMLElement): void {
    this.openRowMenu.set(null);
    if (restoreFocus) {
      trigger?.focus();
    }
  }

  /** Guarded like onMissingClickOutside: every row hears the same document Escape. */
  onRowMenuClickOutside(
    reason: ClickOutsideReason,
    listItemId: string,
    trigger: HTMLElement,
  ): void {
    if (this.openRowMenu() !== listItemId) {
      return;
    }
    this.closeRowMenu(reason === "escape", trigger);
  }

  /** Arrow/Home/End move focus between the open row menu's enabled items (wrapping). */
  onRowMenuKeydown(event: KeyboardEvent, trigger: HTMLElement): void {
    const items = this.rowMenuItems(trigger);
    if (items.length === 0) {
      return;
    }
    const current = items.indexOf(document.activeElement as HTMLElement);
    let next: number;
    switch (event.key) {
      case "ArrowDown":
        next = (current + 1) % items.length;
        break;
      case "ArrowUp":
        next = (current - 1 + items.length) % items.length;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = items.length - 1;
        break;
      case "Tab":
        // Leaving the menu closes it, like a native menu. Focus goes back to the
        // trigger first so the default Tab continues from there, not from a
        // menu item that is about to be removed from the DOM.
        this.closeRowMenu(true, trigger);
        return;
      default:
        return;
    }
    event.preventDefault();
    items[next].focus();
  }

  private rowMenuItems(trigger: HTMLElement): HTMLElement[] {
    return Array.from(
      trigger.parentElement?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ??
        [],
    );
  }

  /** Switches the active store tab, which drives the price chip shown per row + the footer total. */
  selectStoreTab(store: GroceryStoreKey | "best"): void {
    this.selectedStore.set(store);
  }

  /**
   * Locks/unlocks a store as the default tab. Locking also switches the active tab to that
   * store. No template calls this since the lock icon was removed from the Price Comparison card.
   */
  toggleDefaultStore(store: GroceryStoreKey): void {
    const next = this.defaultStore() === store ? null : store;
    this.defaultStore.set(next);
    if (next) {
      this.selectedStore.set(next);
    }
  }

  /** The row's cheapest-store chip (the one it highlights), or null when it has no price. */
  cheapestChip(row: ShoppingRow): StorePriceChip | null {
    return row.chips.find((chip) => chip.isHighlighted) ?? null;
  }

  /** The price chip to show on a row for the active store tab, or null if that store doesn't carry it. */
  selectedChip(row: ShoppingRow): StorePriceChip | null {
    return row.chips.find((chip) => chip.store === this.selectedStore()) ?? null;
  }

  /**
   * The single chip the Archived Lists view's Current list card shows: the active store
   * tab's chip, or — under "Best", or when that store doesn't carry the item — the
   * cheapest (highlighted) one, so the card isn't blank on the default "Best" tab.
   */
  currentListChip(row: ShoppingRow): StorePriceChip | null {
    return this.selectedChip(row) ?? row.chips.find((chip) => chip.isHighlighted) ?? null;
  }
}
